import React, { useEffect, useState, useCallback } from "react";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { api } from "./lib/api";
import { Header, MobileTabs } from "./components/Header";
import { PausedBanner } from "./components/PausedBanner";
import { SendLockBanner } from "./components/SendLockBanner";
import { UserManual } from "./components/UserManual";
import { ControlPanel } from "./components/ControlPanel";
import { Today } from "./screens/Today";
import { TalkToKurt } from "./screens/TalkToKurt";
import { Opportunities } from "./screens/Opportunities";
import { Approve } from "./screens/Approve";
import { CoverChain } from "./screens/CoverChain";
import { SeoEngine } from "./screens/SeoEngine";
import { TrafficEngine } from "./screens/TrafficEngine";
import { SalesLearning } from "./screens/SalesLearning";
import { Inbox } from "./screens/Inbox";
import { Login } from "./screens/Login";

export default function App() {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [tab, setTab] = useState("talk");
  const [business, setBusiness] = useState(null);
  const [settings, setSettings] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const [controlOpen, setControlOpen] = useState(false);

  const refresh = useCallback(async () => {
    const b = await api.bootstrap();
    setBusiness(b.business);
    setSettings(b.settings);
    setPendingCount(b.counts.pending_actions);
  }, []);

  useEffect(() => {
    api.me().then((u) => setUser(u)).catch(() => setUser(false));
  }, []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  const logout = async () => { try { await api.logout(); } catch { /* ignore */ } setUser(false); };

  if (user === null) {
    return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center"><span className="text-slate-500 text-sm">Loading…</span></div>;
  }
  if (!user) return <Login onAuthed={setUser} />;

  const paused = settings?.paused || false;
  const togglePause = async () => { if (paused) await api.resume(); else await api.pause(); refresh(); };

  return (
    <div className="App min-h-screen bg-[#0B0F17] text-slate-100">
      <Header tab={tab} setTab={setTab} paused={paused} onTogglePause={togglePause} pendingCount={pendingCount}
        onOpenManual={() => setManualOpen(true)} onOpenControl={() => setControlOpen(true)} onLogout={logout} />
      <MobileTabs tab={tab} setTab={setTab} pendingCount={pendingCount} />

      <main className="px-4 md:px-6 py-8">
        <SendLockBanner locked={settings?.send_lock ?? true} onChange={refresh} />
        {paused && <div className="max-w-6xl mx-auto"><PausedBanner onResume={togglePause} /></div>}

        {tab === "talk" && <TalkToKurt business={business} settings={settings} goOpportunities={() => setTab("opportunities")} />}
        {tab === "today" && <Today goOpportunities={() => setTab("opportunities")} goApprove={() => setTab("approve")} />}
        {tab === "opportunities" && <Opportunities goApprove={() => setTab("approve")} />}
        {tab === "approve" && <Approve onChange={refresh} />}
        {tab === "inbox" && <Inbox />}
        {tab === "covers" && <CoverChain />}
        {tab === "traffic" && <TrafficEngine />}
        {tab === "seo" && <SeoEngine />}
        {tab === "sales" && <SalesLearning />}
      </main>

      <UserManual open={manualOpen} onOpenChange={setManualOpen} />
      <ControlPanel open={controlOpen} onOpenChange={setControlOpen} settings={settings} business={business}
        refresh={refresh} paused={paused} onTogglePause={togglePause} />
      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
